import React, { useCallback } from 'react';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { SingleField } from '../fields/SingleField';
import { getOpenFieldsAndSections, toggleOpenFieldSection } from '../store';
import { MarginArrangements, UpdatedOpinionField } from '../store/types';
import styles from './OpinionSection.module.scss';
import { SectionWrapper } from './SectionWrapper';

interface MarginArrangementsProps {
    marginArrangements: MarginArrangements;
    previousMarginArrangements: MarginArrangements | null;
    isEditing: boolean;
    isUpdating: boolean;
    fieldsUpdated: UpdatedOpinionField[];
    nettingEngineFields?: string[];
}

export const MarginArrangementsSection: React.FC<MarginArrangementsProps> = ({ marginArrangements, previousMarginArrangements, isEditing, isUpdating, fieldsUpdated, nettingEngineFields }) => {
    const dispatch = useAppDispatch();
    const openFieldsAndSections = useAppSelector(getOpenFieldsAndSections);
    const getFieldOpen = useCallback((id: string) => openFieldsAndSections.includes(id), [openFieldsAndSections]);
    const toggleFieldOpen = useCallback((id: string) => dispatch(toggleOpenFieldSection(id)), [dispatch]);
    const sectionId = 'marginArrangements';
    const { recharacterisedSecurityInterest, recharacterisationAffectsEnforceability, actionsContinuedTitle, otherToEnsureValidity, transferorRightToExchange, alternativeMarginMethods, transfereeConsent, transfereeRightsInsolvency, clawbackDuringSuspectPeriod } = marginArrangements;

    const getPreviousField = useCallback((fieldId: keyof MarginArrangements) => !isNull(previousMarginArrangements) && previousMarginArrangements[fieldId] || null, [previousMarginArrangements]);

    return (
        <SectionWrapper id={sectionId} label='Margin Arrangements' isEditing={isEditing} isUpdating={isUpdating}>
            <div className={styles.marginArrangementsSectionWrapper}>
                <SingleField
                    field={recharacterisedSecurityInterest}
                    previousField={getPreviousField('recharacterisedSecurityInterest')}
                    fieldId='recharacterisedSecurityInterest'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={recharacterisationAffectsEnforceability}
                    previousField={getPreviousField('recharacterisationAffectsEnforceability')}
                    fieldId='recharacterisationAffectsEnforceability'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No', 'Not Applicable']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={actionsContinuedTitle}
                    previousField={getPreviousField('actionsContinuedTitle')}
                    fieldId='actionsContinuedTitle'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={otherToEnsureValidity}
                    previousField={getPreviousField('otherToEnsureValidity')}
                    fieldId='otherToEnsureValidity'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={transferorRightToExchange}
                    previousField={getPreviousField('transferorRightToExchange')}
                    fieldId='transferorRightToExchange'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
                <SingleField
                    field={alternativeMarginMethods}
                    previousField={getPreviousField('alternativeMarginMethods')}
                    fieldId='alternativeMarginMethods'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={transfereeConsent}
                    previousField={getPreviousField('transfereeConsent')}
                    fieldId='transfereeConsent'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                />
                <SingleField
                    field={transfereeRightsInsolvency}
                    previousField={getPreviousField('transfereeRightsInsolvency')}
                    fieldId='transfereeRightsInsolvency'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    nettingEngineFields={nettingEngineFields}
                />
                <SingleField
                    field={clawbackDuringSuspectPeriod}
                    previousField={getPreviousField('clawbackDuringSuspectPeriod')}
                    fieldId='clawbackDuringSuspectPeriod'
                    sectionId={sectionId}
                    isEditing={isEditing}
                    isUpdating={isUpdating}
                    fieldsUpdated={fieldsUpdated}
                    getFieldOpen={getFieldOpen}
                    toggleFieldOpen={toggleFieldOpen}
                    positiveAnswers={['No']}
                    negativeAnswers={['Yes', 'Yes - with exceptions']}
                />
            </div>
        </SectionWrapper>
    );
};
